import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { Sparkles, Pencil, Trash2, ArrowRightLeft, ClipboardList, RotateCcw } from 'lucide-react';
import { apiClient } from '../../../packages/api-client';
import { StateView } from '../../../packages/ui';
import { TimeMachineView } from '../TimeMachinePage';
import '../../../components/ChartFigure.css';

/** Mirrors backend internal/events.ProjectActivity. */
export interface ProjectActivityEntry {
    id: string;
    event_type: string;
    actor_id?: string;
    actor_first_name?: string;
    actor_last_name?: string;
    actor_email?: string;
    object_type: string;
    task_id?: string;
    changes: Record<string, unknown>;
    created_at: string;
}

interface ActivityPage {
    data: ProjectActivityEntry[];
    meta: { page: number; limit: number; total: number; total_pages: number };
}

const ICONS: Record<string, LucideIcon> = {
    task_created: Sparkles, task_updated: Pencil, task_deleted: Trash2,
    task_moved: ArrowRightLeft, task_restored: RotateCcw,
};

const words = (v: unknown) => String(v ?? '').replace(/_/g, ' ');

function verb(e: ProjectActivityEntry): string {
    switch (e.event_type) {
        case 'task_created': return 'created';
        case 'task_updated': return 'updated';
        case 'task_moved': return 'moved';
        case 'task_deleted': return 'deleted';
        case 'task_restored': return 'restored';
        default: return words(e.event_type);
    }
}

function actorName(e: ProjectActivityEntry) {
    const full = `${e.actor_first_name ?? ''} ${e.actor_last_name ?? ''}`.trim();
    return full || e.actor_email || (e.actor_id ? 'A former member' : 'System');
}

/**
 * Project › Activity: the attributable change feed (GET /projects/{id}/activity,
 * scoped like the time machine) and, separately, History — the read-only
 * Time Machine snapshot view.
 */
export function ActivitySection({ projectId }: { projectId: string }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <div>
                <section aria-labelledby="act-feed-h" className="pd-panel">
                    <div className="pd-panel-heading">
                        <h2 id="act-feed-h">Task updates</h2>
                    </div>
                    <div style={{ padding: 'var(--space-4) var(--space-5)' }}>
                        <ProjectActivityFeed projectId={projectId} />
                    </div>
                </section>
                <p className="pd-note">Activity is shown only for work you can access. Open a task to inspect its context and evidence.</p>
            </div>
            <TimeMachineView projectId={projectId} />
        </div>
    );
}

export function ProjectActivityFeed({ projectId }: { projectId: string }) {
    const [entries, setEntries] = useState<ProjectActivityEntry[]>([]);
    const [meta, setMeta] = useState<ActivityPage['meta'] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const current = useRef(projectId);
    current.current = projectId;

    const load = useCallback((page: number) => {
        setLoading(true); setError(false);
        apiClient.get<ActivityPage>(`/projects/${projectId}/activity`, { params: { page } })
            .then(r => {
                if (current.current !== projectId) return;
                setEntries(prev => (page === 1 ? r.data.data : [...prev, ...r.data.data]));
                setMeta(r.data.meta);
            })
            .catch(() => { if (current.current === projectId) setError(true); })
            .finally(() => { if (current.current === projectId) setLoading(false); });
    }, [projectId]);

    useEffect(() => { setEntries([]); setMeta(null); load(1); }, [load]);

    if (error && entries.length === 0) {
        return <StateView state="error" title="Activity couldn't be loaded" message="The activity request failed; this is not an empty history." onRetry={() => load(1)} />;
    }
    if (loading && entries.length === 0) return <StateView state="loading" label="Loading activity" />;
    if (entries.length === 0) return <StateView state="empty" title="No activity yet" message="Task changes in this project will appear here." />;

    const hasMore = meta !== null && meta.page < meta.total_pages;
    return (
        <div>
            <p className="rpt-muted" style={{ marginBottom: 'var(--space-2)' }}>{meta?.total ?? entries.length} changes, newest first</p>
            <ol className="act-feed">
                {entries.map(e => {
                    const Icon = ICONS[e.event_type] ?? ClipboardList;
                    const title = e.changes.title ? String(e.changes.title) : 'a task';
                    const to = e.changes.new_status ? ` to ${words(e.changes.new_status)}` : '';
                    const at = new Date(e.created_at);
                    return (
                        <li key={e.id} className="act-item">
                            <span className="act-icon"><Icon size={14} aria-hidden="true" /></span>
                            <span className="act-body">
                                <strong>{actorName(e)}</strong> {verb(e)}{' '}
                                {e.task_id && e.event_type !== 'task_deleted' ? <Link to={`/tasks/${e.task_id}`}>{title}</Link> : <span>{title}</span>}
                                {to}
                            </span>
                            <time className="act-time" dateTime={e.created_at}>{at.toLocaleDateString()} {at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>
                        </li>
                    );
                })}
            </ol>
            {error && <p role="alert" className="rpt-muted">Couldn&apos;t load more activity.</p>}
            {hasMore && (
                <button type="button" className="btn btn-ghost btn-sm" disabled={loading} onClick={() => load(meta.page + 1)} style={{ marginTop: 'var(--space-2)' }}>
                    {loading ? 'Loading…' : 'Load more'}
                </button>
            )}
        </div>
    );
}
